import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { signOut, type User } from 'firebase/auth';
import { auth } from '../firebase';
import { apiPost } from './api';

let generation = 0;
let queue: Promise<unknown> = Promise.resolve();
let listeners: PluginListenerHandle[] = [];
let activeUid: string | null = null;

function serial<T>(work: () => Promise<T>): Promise<T> {
    const task = queue.then(work, work);
    queue = task.catch(() => undefined);
    return task;
}
function deviceId(): string {
    const key = 'parentscheck-push-device';
    let value = localStorage.getItem(key);
    if (!value) { value = crypto.randomUUID(); localStorage.setItem(key, value); }
    return value;
}
async function removeListeners() {
    const previous = listeners;
    listeners = [];
    await Promise.all(previous.map(handle => handle.remove()));
}
async function clearDevice() {
    await removeListeners();
    await FirebaseMessaging.deleteToken();
    await FirebaseMessaging.removeAllDeliveredNotifications();
}

/** Captures the authenticated owner and serializes token mutations with sign-out. */
export async function registerPushNotifications(user: User, onTap?: (data: Record<string, unknown>) => void) {
    if (!Capacitor.isNativePlatform() || auth.currentUser?.uid !== user.uid) return;
    if (activeUid === user.uid) return;
    activeUid = user.uid;
    const epoch = ++generation;
    const current = () => epoch === generation && auth.currentUser?.uid === user.uid;
    const save = async (token: string) => {
        if (!current()) return;
        await apiPost(user, '/update-fcm-token', {
            fcmToken: token, deviceId: deviceId(),
            uiLanguage: (localStorage.getItem('i18nextLng') || navigator.language).split('-')[0]
        });
    };
    try {
        await serial(async () => {
            await removeListeners();
            if (!current()) return;
            let permission = await FirebaseMessaging.checkPermissions();
            if (!current()) return;
            if (permission.receive === 'prompt') permission = await FirebaseMessaging.requestPermissions();
            if (!current() || permission.receive !== 'granted') return;
            const add = async (promise: Promise<PluginListenerHandle>) => {
                const handle = await promise;
                if (current()) listeners.push(handle); else await handle.remove();
            };
            await add(App.addListener('appStateChange', ({ isActive }) => {
                if (current() && isActive) void FirebaseMessaging.removeAllDeliveredNotifications().catch(() => undefined);
            }));
            if (!current()) return;
            await add(FirebaseMessaging.addListener('tokenReceived', event => {
                void serial(() => save(event.token)).catch(() => console.warn('[Push] Token refresh failed'));
            }));
            if (!current()) return;
            await add(FirebaseMessaging.addListener('notificationActionPerformed', event => {
                const data = event.notification?.data as Record<string, unknown> | undefined;
                if (current() && data?.ownerId === user.uid) onTap?.(data);
            }));
            if (!current()) return;
            const { token } = await FirebaseMessaging.getToken();
            await save(token);
        });
    } catch {
        if (current()) activeUid = null; // Permit a later registration retry.
        console.warn('[Push] Registration failed');
    }
}

/** Normal logout must revoke this device before dropping its auth credentials. */
export async function signOutSafely() {
    const user = auth.currentUser;
    ++generation; activeUid = null;
    if (Capacitor.isNativePlatform()) await serial(async () => {
        await removeListeners();
        if (user) await apiPost(user, '/unregister-fcm-token', { deviceId: deviceId() });
        await clearDevice();
    });
    await signOut(auth);
}

/** Auth revoked/deleted elsewhere: invalidate queued setup and the native token. */
export async function clearPushOnAuthLoss() {
    ++generation; activeUid = null;
    if (Capacitor.isNativePlatform()) await serial(clearDevice);
}

export async function signOutAfterDeletion() {
    await clearPushOnAuthLoss();
    await signOut(auth);
}
